import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Chip, Grid, TextField, Typography } from "@material-ui/core";
import { Equalizer, Send } from "@material-ui/icons";

import { submitProgressReportInternal } from "../../../../reducer/internalResearch/action";
import { uploadMultiFile } from "../../../../reducer/file/action";
import MultiFileManager from "../../../File/MultiFileManager/MultiFileManager";
import select from "../../../../utils/select";
import SectionCard from "../../utils/SectionCard";
import { INT_DURATION_TWO_YEAR } from "../../../../constants/internalResearchStatus";

const STATUS_LABEL = {
  draft:          "Nháp",
  submitted:      "Chờ duyệt",
  accepted:       "Đạt",
  adjust_request: "Yêu cầu sửa",
  rejected:       "Không đạt",
};

const STATUS_COLOR = {
  draft:          "#9e9e9e",
  submitted:      "#1976d2",
  accepted:       "#4caf50",
  adjust_request: "#ff9800",
  rejected:       "#f44336",
};

// ProgressReportForm — CN nộp Báo cáo tiến độ (chỉ đề tài 2y, 1 row / đề tài).
// 4 ô text + file optional. Resubmit cho phép khi status = adjust_request.
// FE check duration để hiển thị banner; BE cũng enforce.
export default function ProgressReportForm({ detail, onDone, readOnly = false }) {
  const dispatch = useDispatch();
  const profileDetail = useSelector((s) => select(s, "profileReducer", "detail"));

  const isTwoYear = detail?.durationMonths === INT_DURATION_TWO_YEAR;
  const report = detail?.progressReport;
  const currentStatus = report?.status || "draft";

  const canSubmit =
    isTwoYear &&
    !readOnly &&
    (currentStatus === "draft" || currentStatus === "adjust_request" || !report);

  const parseUrls = (s) => (s ? String(s).split("||").filter(Boolean) : []);
  const [fields, setFields] = useState({
    completedWork:  report?.completedWork  || "",
    evaluation:     report?.evaluation     || "",
    nextPlan:       report?.nextPlan       || "",
    recommendation: report?.recommendation || "",
  });
  const [fileUrls, setFileUrls] = useState(parseUrls(report?.fileLink));
  const [newFiles, setNewFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const requiredFilled =
    fields.completedWork.trim() &&
    fields.evaluation.trim() &&
    fields.nextPlan.trim();

  const commitFiles = async () => {
    if (!newFiles || newFiles.length === 0) return fileUrls.join("||");
    const ownerId = profileDetail?.id || 0;
    const result = await dispatch(uploadMultiFile(ownerId, newFiles, "internalResearch"));
    if (result?.success && Array.isArray(result.data)) {
      const merged = [...fileUrls, ...result.data];
      setFileUrls(merged);
      setNewFiles([]);
      return merged.join("||");
    }
    return fileUrls.join("||");
  };

  const handleSubmit = async () => {
    if (!requiredFilled) return;
    setSaving(true);
    try {
      const fileLink = await commitFiles();
      const ok = await dispatch(
        submitProgressReportInternal(detail.id, { ...fields, fileLink })
      );
      if (ok && onDone) onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box padding="16px 24px">
      <SectionCard
        icon={<Equalizer />}
        title={
          <Box display="flex" alignItems="center" gridGap={12}>
            <span>Báo cáo tiến độ (BCTĐ)</span>
            <Chip
              size="small"
              label={STATUS_LABEL[currentStatus]}
              style={{
                backgroundColor: STATUS_COLOR[currentStatus],
                color: "#fff",
                height: 22,
                fontSize: 12,
              }}
            />
          </Box>
        }
        marginTop={0}
      >
        {!isTwoYear && (
          <Box padding="10px 14px" bgcolor="#fff3e0" border="solid 1px #fb8c00" borderRadius={4} marginBottom={2}>
            <Typography variant="body2">
              BCTĐ chỉ áp dụng cho đề tài <b>2 năm (24 tháng)</b>. Đề tài 1 năm
              không nộp BCTĐ — HĐ tiến độ sẽ tự đánh giá.
            </Typography>
          </Box>
        )}
        {report?.reviewComment && (
          <Box marginBottom={2} padding="8px 12px" bgcolor="#fafafa" borderRadius={4} border="solid 1px #e0e0e0">
            <Typography variant="caption" color="textSecondary"><b>Nhận xét hội đồng:</b></Typography>
            <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{report.reviewComment}</Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          {[
            { label: "1. Công việc đã hoàn thành", key: "completedWork",  required: true },
            { label: "2. Đánh giá tiến độ",        key: "evaluation",     required: true },
            { label: "3. Kế hoạch tiếp theo",      key: "nextPlan",       required: true },
            { label: "4. Kiến nghị",               key: "recommendation", required: false },
          ].map((f) => (
            <Grid item xs={12} key={f.key}>
              <Typography variant="body2" style={{ marginBottom: 6 }}>
                <b>{f.label}</b>
                {f.required && <span style={{ color: "#c62828" }}> *</span>}
              </Typography>
              <TextField
                fullWidth multiline minRows={3} variant="outlined"
                value={fields[f.key]}
                disabled={!canSubmit}
                onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>File đính kèm (PDF/DOCX) — không bắt buộc</b>
            </Typography>
            <MultiFileManager
              fileUrls={fileUrls}
              setFileUrls={setFileUrls}
              newFiles={newFiles}
              setNewFiles={setNewFiles}
              disabled={!canSubmit}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {canSubmit && (
        <Box marginTop={3} paddingTop={2} display="flex" justifyContent="flex-end" borderTop="solid 1px #e0e0e0">
          <Button
            variant="contained" color="primary"
            startIcon={<Send />} disabled={saving || !requiredFilled}
            onClick={handleSubmit}
          >
            {currentStatus === "adjust_request" ? "Gửi lại sau chỉnh sửa" : "Nộp BCTĐ"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
